import { memo, useCallback, useState } from "react";
import { useParams } from "react-router-dom";
import { useSalesContract } from "../../../../../store/contexts/SalesContractContext";
import { useTranslation } from "react-i18next";
import {
  AutoComplete,
  DateInput,
  FormContainer,
  ISelectType,
  SwitchInput,
  Treturn,
  useFetch,
} from "@4uhub/lib4uhub";
import { Grid } from "@mui/material";
import {
  salesContractHcpInclusionRuleSchema,
  TSalesContractHcpInclusionRuleForm,
} from "./salesContractHcpInclusionRuleSchema";
import { SalesContractHcpInclusionRuleProps } from "./model";
import {
  createSalesContractHcpInclusionRule,
  findSalesContractHcpInclusionRuleById,
  updateSalesContractHcpInclusionRule,
} from "../../../../../services/salesContractHcpInclusionRule.service";

import MainApi from "../../../../../services/mainApi.service";
const translationPath =
  "page.4uSalesContract.registers.salesContractHcpInclusionRule.";

const HEALTH_PROGRAM_TYPE_ROUTE =
  process.env.REACT_APP_MAIN + "/api/v1/HealthProgram/";
const healthProgramTypeService = new MainApi<ISelectType>(
  HEALTH_PROGRAM_TYPE_ROUTE
);

export const SalesContractHcpInclusionRule = memo(
  ({ id }: SalesContractHcpInclusionRuleProps) => {
    const { id: salesContractId } = useParams();
    const { onClose, onRefresh } = useSalesContract();
    const { t } = useTranslation();

    const { sendRequest: save, loading: sLoading } = useFetch(
      createSalesContractHcpInclusionRule
    );

    const { sendRequest: update, loading: uLoading } = useFetch(
      updateSalesContractHcpInclusionRule
    );

    const { sendRequest: get, loading: gLoading } = useFetch(
      findSalesContractHcpInclusionRuleById
    );

    const handleEntity = (data: any, type: "QPF" | "QPJ") => {
      if (!data) return null;

      switch (type) {
        case "QPJ":
          return {
            ...data,
            corporateName: data?.corporateName,
          };
        default:
          return {
            ...data,
            individualName: data?.individualName,
          };
      }
    };

    const updateHandler = (
      v: TSalesContractHcpInclusionRuleForm,
      id: string
    ) => {
      return update({
        item: {
          id: id,
          initialDate: v.initialDate.toISOString().substring(0, 10),
          endDate: v.endDate.toISOString().substring(0, 10),
          healthProgramId: v.healthProgram.id,
          enable: v.enable,
          salesContractId: salesContractId ?? "",
        },
        id: id,
      });
    };

    const saveHandler = (v: TSalesContractHcpInclusionRuleForm) =>
      save({
        initialDate: v.initialDate.toISOString().substring(0, 10),
        endDate: v.endDate.toISOString().substring(0, 10),
        healthProgramId: v.healthProgram.id,
        enable: v.enable,
        salesContractId: salesContractId ?? "",
      });

    const getHandler = useCallback(
      async (
        id: string
      ): Promise<Treturn<TSalesContractHcpInclusionRuleForm>> => {
        const { data, success } = await get(id);

        if (data) {
          const newData: TSalesContractHcpInclusionRuleForm = {
            ...data,
            initialDate: new Date(`${data.initialDate}`),
            endDate: new Date(`${data.endDate}`),
            healthProgram: {
              id: data.healthProgram.id,
              name: data.healthProgram.name,
              code: Number(data.healthProgram.code),
            },
            enable: data.enable,
          };
          return { data: newData, success: true };
        }
        return { data, success };
      },
      [get]
    );

    const successHandle = () => {
      onClose();
      onRefresh();
    };

    const loading = sLoading || uLoading || gLoading;
    return (
      <FormContainer<TSalesContractHcpInclusionRuleForm>
        title={t(translationPath + "title")}
        subtitle=""
        getFunction={getHandler}
        saveFunction={saveHandler}
        updateFunction={updateHandler}
        onSuccess={successHandle}
        showBackButton={false}
        redirectOnSave={false}
        backButtonOnClick={onClose}
        isModalForm={true}
        propId={id}
        loading={loading}
        schema={salesContractHcpInclusionRuleSchema}
        marginBottom={0}
        customHeight={"fit-content"}
        formActionsOnBottom={false}
        fields={() => (
          <Grid container spacing={2}>
            <Grid item xs={12} sm={12} textAlign="end">
              <SwitchInput
                name="enable"
                label={t(translationPath + "enable") || "Enable"}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <DateInput
                name="initialDate"
                label={t(translationPath + "initial_date") || "Initial Date"}
                autoFocus
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <DateInput
                name="endDate"
                label={t(translationPath + "end_date") || "End Date"}
              />
            </Grid>
            <Grid item xs={12} sm={12}>
              <AutoComplete
                getOptionLabel={(option) => {
                  return option.name;
                }}
                size="small"
                label={
                  t(translationPath + "health_program") || "Health Program"
                }
                name="healthProgram"
                request={healthProgramTypeService.list}
              />
            </Grid>
          </Grid>
        )}
      />
    );
  }
);
